import { Stars02 } from '@untitledui/icons';
import { useCallback, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';

import { OverviewGroupLabel } from './OverviewGroupLabel';

const READ_MORE_LIMIT = 280;

/**
 * The AI summary leads the overview, so it reads best as a clean labeled
 * *text section* (like Lightfield's "About their business") rather than a
 * bordered card — an outlined, mostly-empty box at the top of the page is the
 * "amateur" look we're moving away from. A micro uppercase label + sparkle
 * marker sits above the content with no surrounding chrome.
 */
export function SummaryCardShell({ children }: { children: React.ReactNode }) {
  return (
    <div>
      <div className='flex items-center gap-1.5 mb-1.5'>
        <OverviewGroupLabel>AI summary</OverviewGroupLabel>
        <Stars02
          className='w-3 h-3 shrink-0 text-fg-quaternary'
          aria-hidden='true'
        />
      </div>
      {children}
    </div>
  );
}

/**
 * Loading placeholder for the AI summary body. Deliberately mirrors the
 * COMPACT empty-state row ("No research yet" + Generate button) rather than a
 * tall multi-line paragraph: the overwhelmingly common resolved state is the
 * empty CTA, so a tall 3-line skeleton would collapse ~40px when it resolves
 * and shove the activity feed below it up the page. Matching the empty-state
 * height keeps loading → empty shift-free. (A genuinely filled summary still
 * grows downward, which reads as content arriving rather than a jump.)
 *
 * Shared by ResearchSummaryPanel (live loading) and CRMDetailSkeleton (full
 * page skeleton) so both render an identical placeholder.
 */
export function SummaryLoadingSkeleton() {
  return (
    <div
      className='flex flex-wrap items-center gap-x-3 gap-y-1'
      role='status'
      aria-busy='true'
      aria-label='Loading summary'
    >
      <div className='h-4 w-28 animate-pulse rounded bg-[var(--color-bg-secondary)]' />
      <div className='h-7 w-32 animate-pulse rounded-lg bg-[var(--color-bg-secondary)]' />
    </div>
  );
}

export function ReadMoreText({ text, orgId }: { text: string; orgId: number }) {
  const trimmed = text.trim();
  const needsTruncation = trimmed.length > READ_MORE_LIMIT;
  const [expanded, setExpanded] = useState(false);
  const toggle = useCallback(() => setExpanded(e => !e), []);
  const shown =
    !needsTruncation || expanded
      ? trimmed
      : trimmed.slice(0, READ_MORE_LIMIT).trimEnd() + '…';

  return (
    <div className='text-body-small text-[var(--color-text-primary)]'>
      <MarkdownContent content={shown} orgId={orgId} size='compact' />
      {needsTruncation && (
        <Button
          color='link-gray'
          size='sm'
          onClick={toggle}
          aria-expanded={expanded}
        >
          {expanded ? 'Read less' : 'Read more'}
        </Button>
      )}
    </div>
  );
}
